<template>
    <div class="distribution-summary-se-card mb-3">
        <!-- Header Start -->
        <div class="summary-toolbar">
            <h3 class="summary-toolbar-title">QR Distribution Summary</h3>

            <div class="summary-toolbar-filters">
                <!-- Dealer search -->
                <div class="summary-search">
                    <input
                        v-model="searchInput"
                        type="text"
                        class="summary-search-input"
                        placeholder="Cari Dealer"
                        aria-label="Cari Dealer"
                    />
                    <i class="fas fa-search summary-search-icon" aria-hidden="true"></i>
                </div>

                <!-- Date range picker -->
                <div class="summary-date">
                    <img src="/icon/iconly-calendar.svg" alt="calendar" class="summary-date-icon" />
                    <flat-pickr ref="datepicker" v-model="selected.date" :config="dateRangeConfig"></flat-pickr>
                </div>

                <!-- Dealer dropdown -->
                <div class="summary-dealer">
                    <MultiSelect
                        v-model="filter.dealer"
                        :options="dealerOptions"
                        :custom-label="formatDealerLabel"
                        track-by="code"
                        class="summary-select"
                        :class="{ 'summary-select--active': !!filter.dealer }"
                        placeholder="Semua Dealer"
                        select-label=""
                        deselect-label=""
                        selected-label=""
                        :allow-empty="true"
                    ></MultiSelect>
                </div>

                <button type="button" class="summary-download" @click="exportToCSV">
                    Download CSV <i class="fas fa-file-download"></i>
                </button>
            </div>
        </div>
        <!-- Header End -->

        <!-- Totals -->
        <div class="summary-totals">
            <div class="summary-total">
                <div class="summary-total-label">Total Pengiriman Dari MD</div>
                <div class="summary-total-value">{{ formatCount(total.received) }}</div>
            </div>
            <div class="summary-total">
                <div class="summary-total-label">Total Pemakaian Dari Dealer</div>
                <div class="summary-total-value">{{ formatCount(total.used) }}</div>
            </div>
        </div>

        <!-- Table Start -->
        <div class="w-100 overflow-auto">
            <table class="table w-100 qtrust-table">
                <thead>
                    <tr>
                        <th v-for="column in columns" :key="column.key">{{ column.label }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in rows" :key="row.label">
                        <td v-for="column in columns" :key="column.key">{{ formatCell(row, column) }}</td>
                    </tr>
                    <tr v-if="!isLoading && rows.length === 0">
                        <td :colspan="columns.length" class="text-center text-muted py-4">
                            Tidak ada data ditemukan
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <!-- Table End -->

        <div class="d-flex justify-content-end align-items-center py-2 px-3" v-if="rows.length">
            <p class="text-muted mb-0" style="font-size: 13px;">Showing {{ (config.page-1)*config.limit+1 }}-{{ (config.page-1)*config.limit+rows.length }} of {{ config.total }} Items</p>
            <div class="d-flex align-items-center text-muted ml-3">
                <button class="btn btn-link px-1" style="color: #627884" :disabled="config.page <= 1" @click="goToPage(1)"><i class="fa fa-angle-double-left"></i></button>
                <button class="btn btn-link px-1" style="color: #627884" :disabled="config.page <= 1" @click="goToPage(config.page - 1)"><i class="fa fa-angle-left"></i></button>
                <p class="px-2 text-dark mb-0" style="font-size: 13px;">{{ config.page }}</p>
                <button class="btn btn-link px-1" style="color: #627884" :disabled="config.page >= config.lastPage" @click="goToPage(config.page + 1)"><i class="fa fa-angle-right"></i></button>
                <button class="btn btn-link px-1" style="color: #627884" :disabled="config.page >= config.lastPage" @click="goToPage(config.lastPage)"><i class="fa fa-angle-double-right"></i></button>
            </div>
        </div>
    </div>
</template>

<script>
    import debounce from 'debounce';
    import moment from 'moment';
    import * as XLSX from 'xlsx';
    import { formatCount } from '@/services/qr-issue';

    const API_DATE_FORMAT = 'YYYY-MM-DD';
    const RANGE_SEPARATOR = ' s/d ';
    const SEARCH_DEBOUNCE_MS = 500;
    const PAGE_LIMIT = 10;

    // Shared by the table and the CSV export so both always agree.
    // "optionalCount" columns show "-" when the backend does not send the field.
    const COLUMNS = [
        { key: 'label', label: 'Nama Dealer', type: 'text' },
        { key: 'received', label: 'Pengiriman Dari MD', type: 'count' },
        { key: 'used', label: 'Pemakaian Dari Dealer', type: 'count' },
        { key: 'sent_pos', label: 'Pengiriman Dari Dealer ke Pos', type: 'optionalCount' },
    ];

    function hasValue (value) {
        return value !== undefined && value !== null;
    }

    export default {
        data () {
            // Default period: the last 30 days through today.
            const start = moment().subtract(30, 'days').format(API_DATE_FORMAT);
            const end = moment().format(API_DATE_FORMAT);
            return {
                columns: COLUMNS,
                rows : [],
                isLoading : false,
                requestId : 0,
                total : {
                    received : 0,
                    used : 0,
                },
                searchInput: '',
                selected: {
                    date: `${start}${RANGE_SEPARATOR}${end}`,
                },
                dateRangeConfig: {
                    mode: 'range',
                    altFormat: 'd-m-Y',
                    dateFormat: 'Y-m-d',
                    altInput: true,
                    altInputClass: 'summary-flatpickr-alt',
                    locale: { rangeSeparator: RANGE_SEPARATOR },
                    onOpen: this.onFlatpickrOpen,
                    onClose: this.onCloseFlatpickr
                },
                filter : {
                    start_date: start,
                    end_date: end,
                    search: '',
                    dealer: null,
                },
                config : {
                    page: 1,
                    limit: PAGE_LIMIT,
                    lastPage: 0,
                    total: 0,
                },
            }
        },
        computed : {
            dealerOptions () {
                return this.$store.getters.getDealer() || [];
            },
            // Request params shared by the table and the CSV export. The backend
            // may not support search / kode_dealer yet; they are still sent.
            query () {
                const params = {
                    start_date: this.filter.start_date,
                    end_date: this.filter.end_date,
                };
                const search = this.filter.search.trim();
                if (search) params.search = search;
                if (this.filter.dealer) params.kode_dealer = this.filter.dealer.code;
                return params;
            },
            queryKey () {
                return JSON.stringify(this.query);
            },
        },
        created () {
            this.applySearch = debounce((value) => {
                this.filter.search = value;
            }, SEARCH_DEBOUNCE_MS);
        },
        beforeDestroy () {
            this.applySearch.clear();
        },
        mounted () {
            this.fetchData();
        },
        watch : {
            searchInput (value) {
                this.applySearch(value);
            },
            queryKey () {
                // The page watcher refetches when the page actually changes.
                if (this.config.page !== 1) {
                    this.config.page = 1;
                    return;
                }
                this.fetchData();
            },
            'config.page' : 'fetchData',
        },
        methods : {
            formatCount,
            formatDealerLabel (dealer) {
                return `${dealer.code} – ${dealer.name}`;
            },
            formatCell (row, column) {
                const value = row[column.key];
                if (column.type === 'count') return formatCount(value);
                if (column.type === 'optionalCount') return hasValue(value) ? formatCount(value) : '-';
                return value || '-';
            },
            goToPage (page) {
                if (page >= 1 && page <= this.config.lastPage) this.config.page = page;
            },
            setDateFilter: debounce((dates, vm) => {
                vm.filter.start_date = dates[0];
                vm.filter.end_date = dates[1] ? dates[1] : dates[0];
            }, 2000),
            async fetchData () {
                // Ignore responses of requests that were superseded meanwhile.
                const requestId = ++this.requestId;
                this.isLoading = true;
                try {
                    const res = await this.$http.get('/api/qrs/issue-dealer', {
                        params: { ...this.query, page: this.config.page, limit: this.config.limit },
                    });
                    if (requestId !== this.requestId) return;
                    const meta = res.data.meta || {};
                    this.rows = Array.isArray(res.data.data) ? res.data.data : [];
                    this.total.received = meta.totalReceived || 0;
                    this.total.used = meta.totalUsed || 0;
                    this.config.total = meta.total || 0;
                    this.config.lastPage = meta.lastPage || 0;
                } catch (err) {
                    console.log(err);
                    if (requestId !== this.requestId) return;
                    this.rows = [];
                    this.total.received = 0;
                    this.total.used = 0;
                    this.config.total = 0;
                    this.config.lastPage = 0;
                } finally {
                    if (requestId === this.requestId) this.isLoading = false;
                }
            },
            async exportToCSV () {
                try {
                    // Fetch every row that matches the current filters.
                    const res = await this.$http.get('/api/qrs/issue-dealer', {
                        params: { ...this.query, page: 1, limit: this.config.total || this.config.limit },
                    });
                    const items = Array.isArray(res.data.data) ? res.data.data : [];
                    const headers = COLUMNS.map(column => column.label);
                    const exportRows = items.map(item => {
                        const newItem = {};
                        COLUMNS.forEach(column => {
                            const value = item[column.key];
                            if (column.type === 'optionalCount') {
                                newItem[column.label] = hasValue(value) ? value : '-';
                            } else if (column.type === 'count') {
                                newItem[column.label] = value || 0;
                            } else {
                                newItem[column.label] = value || '-';
                            }
                        });
                        return newItem;
                    });
                    // The header option keeps the header row when there are no rows.
                    const worksheet = XLSX.utils.json_to_sheet(exportRows, { header: headers });

                    const workbook = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

                    const filename = `distribution_summary_${this.filter.start_date}_${this.filter.end_date}.csv`;

                    XLSX.writeFile(workbook, filename, { bookType: 'csv' });
                } catch (err) {
                    console.log('Export error:', err);
                }
            },
            onCloseFlatpickr (dates) {
                if (dates.length === 2) {
                    const formatDate = (date) => moment(date).format(API_DATE_FORMAT);
                    this.setDateFilter([formatDate(dates[0]), formatDate(dates[1])], this);
                    this.afterSelectedFilter = true;
                }
            },
            onFlatpickrOpen () {
                if (this.afterSelectedFilter) {
                    this.$refs.datepicker.fp.close();
                    this.afterSelectedFilter = false;
                }
            },
        },
    }
</script>

<style>
    /* MultiSelect and flat-pickr render their own DOM, so these cannot be scoped.
       Everything hangs off .distribution-summary-se-card because global
       .qtrust-select / .flatpickr-alt rules exist on other pages. */
    .distribution-summary-se-card .summary-select.multiselect {
        min-height: 0;
        font-size: 12px;
        color: #2F2F2F;
    }

    .distribution-summary-se-card .summary-select .multiselect__tags {
        display: flex;
        align-items: center;
        min-height: 32px;
        height: 32px;
        padding: 0 32px 0 12px;
        background-color: #FFFFFF;
        border: 1px solid #F3F3F3;
        border-radius: 4px;
    }

    .distribution-summary-se-card .summary-select .multiselect__placeholder,
    .distribution-summary-se-card .summary-select .multiselect__single,
    .distribution-summary-se-card .summary-select .multiselect__input {
        min-height: 0;
        margin: 0;
        padding: 0;
        font-size: 12px;
        line-height: 30px;
        color: #2F2F2F;
        background: transparent;
    }

    .distribution-summary-se-card .summary-select .multiselect__single {
        display: block;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .distribution-summary-se-card .summary-select .multiselect__select {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 30px;
        padding: 0;
        line-height: 1;
    }

    .distribution-summary-se-card .summary-select .multiselect__select::before {
        position: static;
        z-index: 1;
        margin: 0;
        border-width: 6px 4px 0;
        border-color: #000000 transparent transparent;
    }

    .distribution-summary-se-card .summary-select .multiselect__content-wrapper {
        min-width: 100%;
        width: max-content;
        max-width: 320px;
        border-color: #F3F3F3;
        z-index: 20;
    }

    .distribution-summary-se-card .summary-select .multiselect__option {
        min-height: 0;
        padding: 9px 12px;
        font-size: 12px;
        line-height: 14px;
    }

    .distribution-summary-se-card .summary-select.summary-select--active .multiselect__tags {
        border-color: #F36F21;
    }

    .distribution-summary-se-card .summary-flatpickr-alt {
        border: none !important;
        outline: none !important;
        font-size: 12px !important;
        background: transparent !important;
        color: #2F2F2F !important;
        min-width: 0 !important;
        width: 150px !important;
        height: 30px !important;
        padding: 0 !important;
        cursor: pointer !important;
    }
</style>

<style scoped>
    .distribution-summary-se-card {
        background-color: #FFFFFF;
        border-radius: 8px;
    }

    /* Toolbar: title on the left, filters + download on the right */
    .summary-toolbar {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 12px 20px;
        padding: 20px 30px;
    }

    .summary-toolbar-title {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: #2F2F2F;
        white-space: nowrap;
    }

    .summary-toolbar-filters {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 12px 16px;
    }

    .summary-search,
    .summary-date,
    .summary-dealer,
    .summary-download {
        height: 32px;
        background-color: #FFFFFF;
        border: 1px solid #F3F3F3;
        border-radius: 4px;
    }

    .summary-search {
        display: flex;
        align-items: center;
        width: 150px;
        padding: 0 12px;
    }

    .summary-search-input {
        flex: 1;
        min-width: 0;
        height: 30px;
        padding: 0;
        border: none;
        outline: none;
        background: transparent;
        font-size: 12px;
        color: #2F2F2F;
    }

    .summary-search-icon {
        margin-left: 8px;
        font-size: 12px;
        color: #2F2F2F;
    }

    .summary-date {
        display: flex;
        align-items: center;
        width: 212px;
        padding: 0 12px;
    }

    .summary-date-icon {
        width: 14px;
        height: 14px;
        margin-right: 9px;
        object-fit: contain;
    }

    .summary-dealer {
        width: 151px;
        border: none;
        white-space: nowrap;
    }

    .summary-download {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 151px;
        padding: 0;
        font-size: 12px;
        color: #2F2F2F;
        cursor: pointer;
    }

    .summary-download i {
        margin-left: 8px;
        font-size: 14px;
        color: #000000;
    }

    .summary-download:hover {
        background-color: #F8F8F8;
    }

    /* Totals */
    .summary-totals {
        display: flex;
        flex-wrap: wrap;
        gap: 12px 40px;
        padding: 16px 30px;
        border-top: 1px solid #F3F3F3;
    }

    .summary-total-label {
        font-size: 14px;
        color: #2F2F2F;
    }

    .summary-total-value {
        font-size: 18px;
        font-weight: 700;
        color: #2F2F2F;
    }

    /* Table */
    .qtrust-table {
        margin-bottom: 0;
    }

    .qtrust-table > thead > tr > th {
        text-align: left;
        border-top: 1px solid #F3F3F3;
        border-bottom: 1px solid #F3F3F3;
        padding: 12px 16px 12px 0;
        height: 40px;
        vertical-align: middle;
        font-size: 12px;
        font-weight: 700;
        color: #2F2F2F;
        white-space: nowrap;
    }

    .qtrust-table > tbody > tr > td {
        text-align: left;
        padding: 12px 16px 12px 0;
        border-top: none;
        border-bottom: 1px solid #F3F3F3;
        height: 40px;
        vertical-align: middle;
        font-size: 12px;
        color: #2F2F2F;
    }

    .qtrust-table > thead > tr > th:first-child,
    .qtrust-table > tbody > tr > td:first-child {
        padding-left: 30px;
    }
</style>
